import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Package, Building2, Eye, Trash2 } from "lucide-react";

/**
 * ProvisionsTable — Tabla de "Provisiones Activas" + diálogo de confirmación de borrado.
 *
 * Extraída 1:1 desde `pages/ProvisionsPage.js`. Renderiza las provisiones ya filtradas
 * (ID, cliente, ubicación, estado con badge, fecha) y las acciones de fila (ver detalle y
 * eliminar con guard RBAC `canDelete`). Incluye el `AlertDialog` de confirmación de borrado.
 *
 * @param {Array}    props.filteredProvisions   Provisiones ya filtradas por búsqueda/estado.
 * @param {Object}   props.statusColors         Mapa estado→clases de color del badge.
 * @param {Object}   props.statusLabels         Mapa estado→etiqueta legible.
 * @param {Function} props.formatDate           (dateStr) => string — formatea fechas.
 * @param {Function} props.openDetailModal      (provision) => void — abre el modal de detalle.
 * @param {Function} props.canDelete            (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.openDeleteDialog     (provision) => void — abre el diálogo de borrado.
 * @param {boolean}  props.deleteDialogOpen     Apertura del diálogo de borrado.
 * @param {Function} props.setDeleteDialogOpen  Setter del flag del diálogo de borrado.
 * @param {Object|null} props.provisionToDelete Provisión pendiente de borrado (para el texto).
 * @param {Function} props.handleDeleteProvision Confirma el borrado permanente.
 */
export const ProvisionsTable = ({
  filteredProvisions,
  statusColors,
  statusLabels,
  formatDate,
  openDetailModal,
  canDelete,
  openDeleteDialog,
  deleteDialogOpen,
  setDeleteDialogOpen,
  provisionToDelete,
  handleDeleteProvision,
}) => {
  return (
    <>
      {/* Provisions Table */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <Package className="h-5 w-5 text-[#03BEE4]" />
            Provisiones Activas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E1E5F]">
                  <TableHead className="text-slate-400">ID</TableHead>
                  <TableHead className="text-slate-400">Cliente</TableHead>
                  <TableHead className="text-slate-400">Ubicación</TableHead>
                  <TableHead className="text-slate-400">Estado</TableHead>
                  <TableHead className="text-slate-400">Fecha</TableHead>
                  <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProvisions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                      No hay provisiones que coincidan con los filtros
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProvisions.map((provision) => {                    
                    return (
                      <TableRow key={provision.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                        <TableCell className="text-[#03BEE4] font-mono text-sm">
                          {provision.provision_id}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-400" />
                            <span className="text-white">{provision.customer_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {provision.location_name || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[provision.status]} text-white`}>
                            {statusLabels[provision.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {formatDate(provision.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDetailModal(provision)}
                              className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            {canDelete("provisions") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteDialog(provision);
                                }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                data-testid={`delete-provision-${provision.provision_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar provisión?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta acción eliminará permanentemente la provisión <span className="text-[#42E695] font-semibold">{provisionToDelete?.provision_id}</span> y todos sus tickets técnicos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProvision}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
